import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Save, RotateCcw, Plus, Trash2, Loader2, Globe, Type, DollarSign, Phone, FileText } from "lucide-react";

const AdminContent = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content/`, { withCredentials: true });
      setContent(res.data);
    } catch (err) {
      toast.error("Erro ao carregar conteúdo");
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.put(`${API}/content/${key}`, { value: content[key] }, { withCredentials: true });
      toast.success("Guardado com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const resetSection = async (key) => {
    if (!confirm("Tens a certeza que queres repor os valores padrão?")) return;
    try {
      const res = await axios.post(`${API}/content/reset/${key}`, {}, { withCredentials: true });
      setContent(prev => ({ ...prev, [key]: res.data.value }));
      toast.success("Valores repostos!");
    } catch (err) {
      toast.error("Erro ao repor valores");
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateArrayItem = (section, index, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const addArrayItem = (section, template) => {
    setContent(prev => ({
      ...prev,
      [section]: [...prev[section], { ...template, id: `new_${Date.now()}` }]
    }));
  };

  const removeArrayItem = (section, index) => {
    setContent(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#6697a4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#3d5a63]">Conteúdo do Site</h1>
          <p className="text-[#94A3B8]">Gere todo o texto, preços e informações da landing page</p>
        </div>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-xl flex-wrap h-auto gap-1">
          <TabsTrigger value="hero" className="rounded-lg data-[state=active]:bg-white gap-2">
            <Type className="h-4 w-4" /> Hero
          </TabsTrigger>
          <TabsTrigger value="about" className="rounded-lg data-[state=active]:bg-white gap-2">
            <FileText className="h-4 w-4" /> Sobre
          </TabsTrigger>
          <TabsTrigger value="rentals" className="rounded-lg data-[state=active]:bg-white gap-2">
            <DollarSign className="h-4 w-4" /> Aluguer
          </TabsTrigger>
          <TabsTrigger value="conditions" className="rounded-lg data-[state=active]:bg-white gap-2">
            <FileText className="h-4 w-4" /> Condições
          </TabsTrigger>
          <TabsTrigger value="contact" className="rounded-lg data-[state=active]:bg-white gap-2">
            <Phone className="h-4 w-4" /> Contacto
          </TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Secção Hero</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => resetSection("hero")}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Repor
                </Button>
                <Button size="sm" onClick={() => saveSection("hero")} disabled={saving.hero} className="bg-[#6697a4] hover:bg-[#3d5a63]">
                  {saving.hero ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Guardar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Badge className="bg-blue-100 text-blue-700">Português</Badge>
                  <div className="space-y-2">
                    <Label>Título Principal</Label>
                    <Input value={content.hero?.title_pt || ""} onChange={(e) => updateField("hero", "title_pt", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Título Destaque</Label>
                    <Input value={content.hero?.title_highlight_pt || ""} onChange={(e) => updateField("hero", "title_highlight_pt", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtítulo</Label>
                    <Textarea value={content.hero?.subtitle_pt || ""} onChange={(e) => updateField("hero", "subtitle_pt", e.target.value)} rows={3} />
                  </div>
                </div>
                <div className="space-y-4">
                  <Badge className="bg-green-100 text-green-700">English</Badge>
                  <div className="space-y-2">
                    <Label>Main Title</Label>
                    <Input value={content.hero?.title_en || ""} onChange={(e) => updateField("hero", "title_en", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Highlight Title</Label>
                    <Input value={content.hero?.title_highlight_en || ""} onChange={(e) => updateField("hero", "title_highlight_en", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtitle</Label>
                    <Textarea value={content.hero?.subtitle_en || ""} onChange={(e) => updateField("hero", "subtitle_en", e.target.value)} rows={3} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* About Section */}
        <TabsContent value="about">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Secção Sobre Nós</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => resetSection("about")}><RotateCcw className="h-4 w-4 mr-1" /> Repor</Button>
                <Button size="sm" onClick={() => saveSection("about")} disabled={saving.about} className="bg-[#6697a4] hover:bg-[#3d5a63]">
                  {saving.about ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Guardar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Badge className="bg-blue-100 text-blue-700">Português</Badge>
                  <div className="space-y-2">
                    <Label>Título</Label>
                    <Input value={content.about?.title_pt || ""} onChange={(e) => updateField("about", "title_pt", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Parágrafo 1</Label>
                    <Textarea value={content.about?.text1_pt || ""} onChange={(e) => updateField("about", "text1_pt", e.target.value)} rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label>Parágrafo 2</Label>
                    <Textarea value={content.about?.text2_pt || ""} onChange={(e) => updateField("about", "text2_pt", e.target.value)} rows={4} />
                  </div>
                </div>
                <div className="space-y-4">
                  <Badge className="bg-green-100 text-green-700">English</Badge>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input value={content.about?.title_en || ""} onChange={(e) => updateField("about", "title_en", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Paragraph 1</Label>
                    <Textarea value={content.about?.text1_en || ""} onChange={(e) => updateField("about", "text1_en", e.target.value)} rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label>Paragraph 2</Label>
                    <Textarea value={content.about?.text2_en || ""} onChange={(e) => updateField("about", "text2_en", e.target.value)} rows={4} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rentals Section */}
        <TabsContent value="rentals">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Aluguer de Material</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => resetSection("rentals")}><RotateCcw className="h-4 w-4 mr-1" /> Repor</Button>
                <Button size="sm" onClick={() => saveSection("rentals")} disabled={saving.rentals} className="bg-[#6697a4] hover:bg-[#3d5a63]">
                  {saving.rentals ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Guardar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {content.rentals?.map((rental, idx) => (
                <div key={rental.id || idx} className="p-4 bg-slate-50 rounded-xl">
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Nome PT</Label>
                      <Input value={rental.name_pt || ""} onChange={(e) => updateArrayItem("rentals", idx, "name_pt", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Name EN</Label>
                      <Input value={rental.name_en || ""} onChange={(e) => updateArrayItem("rentals", idx, "name_en", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição PT</Label>
                      <Input value={rental.desc_pt || ""} onChange={(e) => updateArrayItem("rentals", idx, "desc_pt", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Description EN</Label>
                      <Input value={rental.desc_en || ""} onChange={(e) => updateArrayItem("rentals", idx, "desc_en", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Preço (€)</Label>
                      <Input type="number" value={rental.price || 0} onChange={(e) => updateArrayItem("rentals", idx, "price", parseInt(e.target.value))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Duração</Label>
                      <Input value={rental.duration || ""} onChange={(e) => updateArrayItem("rentals", idx, "duration", e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={() => addArrayItem("rentals", { name_pt: "", name_en: "", desc_pt: "", desc_en: "", price: 0, duration: "3H" })}>
                <Plus className="h-4 w-4 mr-1" /> Adicionar Opção
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Conditions Section */}
        <TabsContent value="conditions">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Condições e Termos</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => resetSection("conditions")}><RotateCcw className="h-4 w-4 mr-1" /> Repor</Button>
                <Button size="sm" onClick={() => saveSection("conditions")} disabled={saving.conditions} className="bg-[#6697a4] hover:bg-[#3d5a63]">
                  {saving.conditions ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Guardar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Badge className="bg-blue-100 text-blue-700">Português</Badge>
                  <div className="space-y-2">
                    <Label>Título</Label>
                    <Input value={content.conditions?.title_pt || ""} onChange={(e) => updateField("conditions", "title_pt", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Itens (um por linha)</Label>
                    <Textarea value={(content.conditions?.items_pt || []).join("\n")} onChange={(e) => updateField("conditions", "items_pt", e.target.value.split("\n"))} rows={6} />
                  </div>
                  <div className="space-y-2">
                    <Label>Métodos de Pagamento</Label>
                    <Input value={content.conditions?.payment_methods_pt || ""} onChange={(e) => updateField("conditions", "payment_methods_pt", e.target.value)} />
                  </div>
                </div>
                <div className="space-y-4">
                  <Badge className="bg-green-100 text-green-700">English</Badge>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input value={content.conditions?.title_en || ""} onChange={(e) => updateField("conditions", "title_en", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Items (one per line)</Label>
                    <Textarea value={(content.conditions?.items_en || []).join("\n")} onChange={(e) => updateField("conditions", "items_en", e.target.value.split("\n"))} rows={6} />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Methods</Label>
                    <Input value={content.conditions?.payment_methods_en || ""} onChange={(e) => updateField("conditions", "payment_methods_en", e.target.value)} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Section */}
        <TabsContent value="contact">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Informações de Contacto</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => resetSection("contact")}><RotateCcw className="h-4 w-4 mr-1" /> Repor</Button>
                <Button size="sm" onClick={() => saveSection("contact")} disabled={saving.contact} className="bg-[#6697a4] hover:bg-[#3d5a63]">
                  {saving.contact ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Guardar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input value={content.contact?.phone || ""} onChange={(e) => updateField("contact", "phone", e.target.value)} placeholder="+351 913 154 508" />
                </div>
                <div className="space-y-2">
                  <Label>WhatsApp (só números)</Label>
                  <Input value={content.contact?.whatsapp || ""} onChange={(e) => updateField("contact", "whatsapp", e.target.value)} placeholder="+351913154508" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={content.contact?.email || ""} onChange={(e) => updateField("contact", "email", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Morada</Label>
                  <Input value={content.contact?.address || ""} onChange={(e) => updateField("contact", "address", e.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Link Google Maps</Label>
                  <Input value={content.contact?.address_maps_url || ""} onChange={(e) => updateField("contact", "address_maps_url", e.target.value)} placeholder="https://www.google.com/maps/..." />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default AdminContent;
